import type { PromoCampaignModel } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { PromoCampaignFormData } from '../model/types'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import { any, prop } from 'lodash/fp'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	mergeBy,
	promoCampaignFormSchema,
	useCreatePromoCampaignMutation,
	useDeletePromoCampaignMutation,
	useUpdatePromoCampaignMutation,
} from '@/entities/promo-campaign'

import { confirm } from '@/shared/components/ui/confirmer'
import { ampli } from '@/shared/lib/ampli'
import Sentry from '@/shared/lib/sentry'
import { DateFormat } from '@/shared/tools/dates'

const onError = (error: Error, t: TFunction) => {
	Sentry.captureException(error)
	toast.error(error?.message ?? t('fatal_error_title'), {
		id: 'promo',
	})
}
function getFormData(promoCampaign?: PromoCampaignModel) {
	return {
		name: promoCampaign?.name ?? '',
		description: promoCampaign?.description ?? '',
		date: {
			start: promoCampaign?.date_start,
			end: promoCampaign?.date_end,
		},
		pos_ids: promoCampaign?.pos_ids ?? [],
		is_active: promoCampaign?.is_active ?? false,
	}
}
export function usePromoCampaignForm({
	promoCampaign,
	mode,
	onFileUploaded,
}: {
	promoCampaign?: PromoCampaignModel
	mode: 'create' | 'update'
	onFileUploaded: () => void
}) {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const createPromoCampaign = useCreatePromoCampaignMutation({
		onError: (error: Error) => onError(error, t),
		onSuccess: (campaign) => {
			queryClient.setQueryData(['promo-campaigns'], mergeBy(campaign))
			ampli.promoCreate()
			toast.success(t('promo_successfully_created'), {
				id: 'promo',
			})
			navigate({ to: `../${campaign.id}` })
		},
	})
	const updatePromoCampaign = useUpdatePromoCampaignMutation({
		onError: (error: Error) => onError(error, t),
		onSuccess: (result, payload) => {
			queryClient.setQueryData(
				['promo-campaigns'],
				(old?: PromoCampaignModel[]) =>
					old?.map((campaign) =>
						payload.promoCampaignId === campaign.id ? result : campaign
					)
			)
			ampli.promoUpdate()
			toast.success(t('promo_successfully_updated'), {
				id: 'promo',
			})
		},
	})
	const deletePromoCampaign = useDeletePromoCampaignMutation({
		onError: (error: Error) => onError(error, t),
		onSuccess: (result, id) => {
			queryClient.setQueryData(
				['promo-campaigns'],
				(old?: PromoCampaignModel[]) =>
					old?.filter((campaign) => campaign.id !== id)
			)
			toast.success(t('success_deleted'), {
				id: 'promo',
			})
			navigate({ to: '..' })
		},
	})
	const isLoading = any(prop('isLoading'), [
		deletePromoCampaign,
		createPromoCampaign,
		updatePromoCampaign,
	])
	const methods = useForm({
		resolver: zodResolver(promoCampaignFormSchema),
		defaultValues: getFormData(promoCampaign),
	})
	const onSubmit = (formData: PromoCampaignFormData) => {
		const body = {
			name: formData.name,
			pos_ids: formData.pos_ids,
			description: formData.description,
			date_start: format(new Date(formData.date.start!), DateFormat.system),
			date_end: format(new Date(formData.date.end!), DateFormat.system),
			is_active: Boolean(formData.is_active),
		}
		mode === 'create' &&
			createPromoCampaign.mutate(body, {
				onSuccess: (promoCampaign) => methods.reset(getFormData(promoCampaign)),
			})
		mode === 'update' &&
			updatePromoCampaign.mutate(
				{
					promoCampaignId: promoCampaign!.id!,
					data: body,
				},
				{
					onSuccess: (promoCampaign) =>
						methods.reset(getFormData(promoCampaign)),
				}
			)
	}
	const handleDelete = () =>
		confirm({
			title: t('promo_confirm_delete_title'),
			description: t('promo_confirm_delete_text'),
			actionText: t('general_delete', {
				ns: 'keys',
			}),
			cancelText: t('general_cancel', {
				ns: 'keys',
			}),
			ActionProps: {
				variant: 'destructive',
			},
		}).then((answer: boolean) => {
			if (answer) {
				ampli.promoDelete()
				toast.loading(t('deleting'), {
					id: 'promo',
				})
				deletePromoCampaign.mutate(promoCampaign!.id)
			}
		})
	const handleFileUploaded = () => {
		toast.success(t('promo_successfully_uploaded_file'), {
			id: 'promo',
		})
		onFileUploaded()
	}

	return {
		isLoading,
		methods,
		onSubmit,
		handleDelete,
		handleFileUploaded,
	}
}
